import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, MessageSquareText } from "lucide-react";
import { whatsappApi } from "@/services/whatsapp-api";
import type { WhatsAppResumoConversa } from "@/types/whatsapp";
import { WhatsAppAguardandoBadge } from "@/components/whatsapp/WhatsAppBadges";
import { WhatsAppTranscriptDialog } from "@/components/whatsapp/WhatsAppTranscriptDialog";
import { WhatsAppLinhaDoTempo } from "@/components/whatsapp/WhatsAppLinhaDoTempo";
import { WhatsAppResumoAcoes } from "@/components/whatsapp/WhatsAppResumoAcoes";
import { DetailSection } from "./DetailSection";
import { formatDateTime } from "@/utils/dateUtils";

/**
 * Em que pé está a conversa, dia a dia. Não passa por aprovação: é leitura,
 * não altera dado do perfil. As datas dizem de quando é a informação e
 * denunciam quando chegou mensagem depois do resumo.
 */
function ResumoDaConversa({
  conversa,
  mostrarContato,
  onVerConversa,
}: {
  conversa: WhatsAppResumoConversa;
  mostrarContato: boolean;
  onVerConversa: (conversationId: string) => void;
}) {
  const mensagensNovas =
    !!conversa.ultima_mensagem_em &&
    new Date(conversa.ultima_mensagem_em) > new Date(conversa.mensagens_ate);
  return (
    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
      {mostrarContato && (
        <div className="text-xs font-medium text-muted-foreground">
          {conversa.contact_name ?? conversa.telefone ?? "Conversa"}
        </div>
      )}
      <WhatsAppLinhaDoTempo historico={conversa.historico} resumo={conversa.resumo} />
      {conversa.ultimo_status && (
        <p className="border-t pt-2 text-sm">
          <span className="font-medium">Status atual da conversa:</span> {conversa.ultimo_status}
        </p>
      )}
      <WhatsAppResumoAcoes conversa={conversa} surface="ficha_freelancer" />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {conversa.aguardando && (
          <WhatsAppAguardandoBadge
            aguardando={conversa.aguardando}
            desde={conversa.mensagens_ate}
          />
        )}
        <span>Informações até {formatDateTime(conversa.mensagens_ate)}</span>
        <span>
          Última mensagem do freelancer:{" "}
          {conversa.ultima_mensagem_contato_em
            ? formatDateTime(conversa.ultima_mensagem_contato_em)
            : "nenhuma"}
        </span>
        <span>Resumo gerado em {formatDateTime(conversa.gerado_em)}</span>
        {mensagensNovas && (
          <span className="font-medium text-amber-700">Chegaram mensagens depois deste resumo</span>
        )}
        {conversa.pode_ver_conversa && (
          <button
            type="button"
            className="flex items-center gap-1 hover:text-foreground"
            onClick={() => onVerConversa(conversa.conversation_id)}
          >
            <MessageSquareText className="h-3 w-3" />
            Ver conversa
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * O que a conversa de WhatsApp diz sobre este freelancer, na ficha dele: só
 * leitura. Aprovar status e negociação fica na tela do WhatsApp (COR-387).
 * Some quando não há resumo, para não virar seção vazia na ficha.
 */
export function FreelancerWhatsAppIA({ freelancerId }: { freelancerId: string }) {
  const [conversaAberta, setConversaAberta] = useState<string | null>(null);

  // 403 de quem não tem a tela não é erro pra mostrar: a seção só não aparece.
  const { data } = useQuery({
    queryKey: ["whatsapp-resumo", freelancerId],
    queryFn: () => whatsappApi.resumoDoFreelancer(freelancerId),
    enabled: Boolean(freelancerId),
    retry: false,
  });

  const conversas = data?.conversas ?? [];
  if (conversas.length === 0) return null;

  return (
    <DetailSection
      title="O que o WhatsApp trouxe"
      icon={MessageCircle}
      description="Em que pé está a conversa com este freelancer, dia a dia."
    >
      <div className="space-y-2">
        {conversas.map((conversa) => (
          <ResumoDaConversa
            key={conversa.conversation_id}
            conversa={conversa}
            mostrarContato={conversas.length > 1}
            onVerConversa={setConversaAberta}
          />
        ))}
      </div>

      <WhatsAppTranscriptDialog
        conversationId={conversaAberta}
        onClose={() => setConversaAberta(null)}
      />
    </DetailSection>
  );
}
